// Browser-side depth models. Files are served same-origin from /models/
// (models/web, populated by models/fetch.sh).

export type Backend = 'webgpu' | 'wasm';

export interface ModelFile {
  path: string;
  sha256: string;
  size: number;
}

export interface WebModel {
  id: string;
  name: string;
  license: string;
  /** Preferred file per execution backend. */
  files: Record<Backend, ModelFile>;
  /** DPT-style preprocessing. */
  size: number;
  multiple: number;
  mean: [number, number, number];
  std: [number, number, number];
  /** Output is relative inverse depth: larger = nearer. */
  output: 'disparity' | 'depth';
}

export const WEB_MODELS: WebModel[] = [
  {
    id: 'depth-anything-v2-small',
    name: 'Depth Anything V2 Small',
    license: 'Apache-2.0',
    files: {
      webgpu: {
        path: 'depth-anything-v2-small/model_fp16.onnx',
        sha256: '2df6223f206b5164e21f664ace61dabeb9bb6a49b8b5a3e00510b4807d0f5b04',
        size: 49642442,
      },
      wasm: {
        path: 'depth-anything-v2-small/model_int8.onnx',
        sha256: '01aa7a23de3f4a0ee1a2bb9997e6918104c85a9f95dea46d27b9b3fb0c6b9001',
        size: 27258801,
      },
    },
    size: 518,
    multiple: 14,
    mean: [0.485, 0.456, 0.406],
    std: [0.229, 0.224, 0.225],
    output: 'disparity',
  },
];

export const DEFAULT_WEB_MODEL = WEB_MODELS[0].id;

/** Click segmentation (SlimSAM-77, Apache-2.0): a compressed SAM with the same prompt interface. */
export const SEGMENT_MODEL = {
  id: 'slimsam-77',
  license: 'Apache-2.0',
  encoder: {
    path: 'slimsam-77/vision_encoder.onnx',
    sha256: '9f8433273a6750b587779baa0cf5508111001bf7e7acfcf585d370139fd366d0',
    size: 23276014,
  },
  decoder: {
    path: 'slimsam-77/prompt_encoder_mask_decoder.onnx',
    sha256: 'f4514391764fbd56e08e119060d874ecd7d52994bfb1968af159e12d4943b5bb',
    size: 16557892,
  },
  side: 1024,
  mean: [0.485, 0.456, 0.406] as const,
  std: [0.229, 0.224, 0.225] as const,
};

export function webModel(id: string): WebModel {
  return WEB_MODELS.find((m) => m.id === id) ?? WEB_MODELS[0];
}

/** DPT resize: keep aspect, scale closest to 1 wins, snap to `multiple`. */
export function dptInputSize(w: number, h: number, size: number, multiple: number): [number, number] {
  let sw = size / w;
  let sh = size / h;
  if (Math.abs(1 - sw) < Math.abs(1 - sh)) sh = sw;
  else sw = sh;
  const snap = (v: number) => Math.max(multiple, Math.round(v / multiple) * multiple);
  return [snap(w * sw), snap(h * sh)];
}
